import type { Destination } from '../destination';
import { lazySdk } from '../lazy-sdk';
import { toVendorEvent } from '../mapping';

declare global {
	interface Window {
		// Google Tag Manager's push-only queue, shared with gtag.js.
		dataLayer?: unknown[];
	}
}

/**
 * Google Tag Manager: every event is pushed to `dataLayer` as
 * `{ event: <name>, ...parameters }` and the tenant's container decides what
 * to do with it. Pass-through: no insights guarantee.
 *
 * A container cannot be unloaded once it is on the page, so switching
 * sharing off stops these pushes but not tags the container fires on its
 * own triggers; those stop on the next launch, when it is not loaded at all.
 */
export function createGtmDestination(gtmId: string): Destination {
	const gtm = lazySdk(
		() => import('react-gtm-module').then((module) => module.default),
		(TagManager) => TagManager.initialize({ gtmId }),
	);

	return {
		name: 'gtm',
		start: gtm.load,
		send: (event) => {
			const { name, params } = toVendorEvent(event);
			gtm.use(() => {
				window.dataLayer ??= [];
				window.dataLayer.push({ event: name, ...params });
			});
		},
	};
}
